import {
  Button,
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { BasicExportModal } from "@src/components/modules/invoicing-plan/List/components/basic-export-modal";
import { DataTable, LoadingOverlay } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { withCan } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { InvoicingPlanLinks } from "../components/links";
import { SummaryLabel } from "./components/summary-label";
import { InvoicingPlanListStore } from "./store";

export const InvoicingPlan = withCan(
  { do: ["billingPlan_listing"] },
  observer(function InvoicingPlan() {
    const appStore = useStore();
    const store = useLocalObservable(
      () => new InvoicingPlanListStore(appStore),
    );

    useUpdateURI(store.searchParamsExtended);

    return (
      <DataTable>
        <DataTable.Filter>
          <FiltersPanelRoot filters={store.where}>
            <VStack align="start" w="full" spacing="4">
              <HStack w="full" spacing="4">
                <InvoicingPlanLinks />

                <PeriodPicker
                  tooltipLabel={<Trans>Issue date</Trans>}
                  value={store.dateRange}
                  onChange={store.setDateRange}
                />

                <FiltersPanelSearch
                  onSearch={store.onSearch}
                  width="full"
                  inputWidth="full"
                  defaultValue={store.searchTerm}
                />

                <Tooltip
                  hasArrow
                  label={
                    store.withPrepayments.value ? (
                      <Trans>
                        Show prepayments when they're used. You'll see revenue
                        gradually, as the credit is spent (actual view)
                      </Trans>
                    ) : (
                      <Trans>
                        Shows full prepayment amounts in the month they were
                        invoiced (cashflow view)
                      </Trans>
                    )
                  }
                  placement="top"
                >
                  <Center minW="fit-content">
                    <Switch
                      alignItems="center"
                      display="inline-flex"
                      minW="fit-content"
                      isChecked={store.withPrepayments.value}
                      onChange={store.toggleWithPrepayments}
                    >
                      <Trans>Show prepayments when used</Trans>
                    </Switch>
                  </Center>
                </Tooltip>

                <HStack spacing="2">
                  <FiltersPanelFilters />

                  <Menu>
                    <MenuButton
                      as={Button}
                      colorScheme="grey"
                      isLoading={
                        store.isExportDetailedLoading ||
                        store.isExportBasicLoading
                      }
                      leftIcon={<Icon name="download-02" />}
                      variant="outline"
                    >
                      <Trans>Export</Trans>
                    </MenuButton>

                    <MenuList>
                      <MenuItem
                        onClick={store.basicExportModalDisclosure.onOpen}
                      >
                        <Trans>Basic</Trans>
                      </MenuItem>
                      <MenuItem onClick={store.handleExportDetailed}>
                        <Trans>Detailed</Trans>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </HStack>
            </VStack>
          </FiltersPanelRoot>
        </DataTable.Filter>

        <DataTable.SummaryBox hideBg>
          <DataTable.SummaryItem
            label={t`Planned`}
            isLoaded={!store.isSummaryLoading}
          >
            <SummaryLabel
              label={t`Total`}
              amount={store.summaryData?.total_planned_amount}
              size="lg"
            />
            <SummaryLabel
              label={t`Our Work`}
              amount={store.summaryData?.total_planned_our_work}
            />
            {store.expenseCategories.map((category) => (
              <SummaryLabel
                key={category.id}
                label={category.name}
                amount={
                  store.summaryData?.total_planned_expense_categories.find(
                    ({ category_id }) => category_id === category.id,
                  )?.amount
                }
              />
            ))}
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            label={t`Invoiced`}
            isLoaded={!store.isSummaryLoading}
          >
            <SummaryLabel
              label={t`Total`}
              amount={store.summaryData?.total_invoiced_amount}
              size="lg"
            />
            <SummaryLabel
              label={t`Our Work`}
              amount={store.summaryData?.total_invoiced_our_work}
            />
            {store.expenseCategories.map((category) => (
              <SummaryLabel
                key={category.id}
                label={category.name}
                amount={
                  store.summaryData?.total_invoiced_expense_categories.find(
                    ({ category_id }) => category_id === category.id,
                  )?.amount
                }
              />
            ))}
            <SummaryLabel
              label={t`Prepayments`}
              amount={store.summaryData?.total_invoiced_prepayments}
            />
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            label={t`Paid`}
            isLoaded={!store.isSummaryLoading}
            coloredValue={store.summaryData?.total_paid_amount ?? 0}
          >
            {currency.formatByWorkspace(
              store.summaryData?.total_paid_amount ?? 0,
            )}
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            label={t`To be paid`}
            isLoaded={!store.isSummaryLoading}
          >
            {currency.formatByWorkspace(
              store.summaryData?.total_unpaid_amount ?? 0,
            )}
          </DataTable.SummaryItem>
        </DataTable.SummaryBox>

        <LoadingOverlay
          isLoading={store.isTableDataLoading}
          position="absolute"
        >
          <DataTable.Body
            tableKey={store.tableKey}
            columns={store.columns}
            data={store.tableData}
            onOrderByChange={store.handleChangeOrderBy}
            orderedBy={store.orderBy.clauses[0]}
          />
        </LoadingOverlay>

        <Pagination store={store.pagination} />

        <BasicExportModal store={store} />
      </DataTable>
    );
  }),
);
